import {
  HorizontalTextAlign,
  ITEM_TYPES,
  ItemType,
  NullableHeightSize,
  Position,
  Size,
  Stroke,
  TemplateState,
  TemplateType,
  VerticalTextAlign,
} from "../types";

type SnapTarget = {
  type: ItemType["type"];
  position: Position;
  size: Size | NullableHeightSize;
  id?: string;
  stroke?: Stroke;
  /** When set, only guides matching this text alignment are exposed on the X axis */
  textAlign?: HorizontalTextAlign;
  /** When set, only guides matching this vertical alignment are exposed on the Y axis */
  verticalAlign?: VerticalTextAlign;
  /** When true (null-height text), only the top-edge guide is exposed on the Y axis */
  hasNullHeight?: boolean;
};

export const collectSnapTargets = (
  templateState: TemplateState,
  chapterType: TemplateType
): SnapTarget[] => {
  const targets: SnapTarget[] = [];

  // items with id
  templateState.images.forEach((img) => {
    if (
      templateState.currentItem?.type === ITEM_TYPES.Image &&
      templateState.currentItem.id === img.id
    )
      return;
    targets.push({
      type: ITEM_TYPES.Image,
      position: img.position,
      size: img.size,
      id: img.id,
      stroke: img.stroke,
    });
  });
  templateState.videos.forEach((vid) => {
    if (
      templateState.currentItem?.type === ITEM_TYPES.Video &&
      templateState.currentItem.id === vid.id
    )
      return;
    targets.push({
      type: ITEM_TYPES.Video,
      position: vid.position,
      size: vid.size,
      id: vid.id,
      stroke: vid.stroke,
    });
  });
  templateState.customTexts.forEach((text) => {
    if (
      templateState.currentItem?.type === ITEM_TYPES.CustomText &&
      templateState.currentItem.id === text.id
    )
      return;
    targets.push({
      type: ITEM_TYPES.CustomText,
      position: text.position,
      size: text.size,
      id: text.id,
      textAlign: text.textAlign,
      verticalAlign: text.verticalAlign,
    });
  });
  templateState.parameteredTexts?.forEach((text) => {
    if (templateState.currentItem?.id === text.id) return;
    targets.push({
      type: ITEM_TYPES.ParameteredText,
      position: text.position,
      size: text.size,
      id: text.id,
      textAlign: text.textAlign,
      verticalAlign: text.verticalAlign,
      hasNullHeight: text.size.height == null,
    });
  });
  templateState.shapes.forEach((shape) => {
    if (templateState.currentItem?.id === shape.id) return;
    targets.push({
      type: ITEM_TYPES.Shape,
      position: shape.position,
      size: shape.size,
      id: shape.id,
      stroke: shape.stroke,
    });
  });
  templateState.slideLayoutShapes?.forEach((shape) => {
    if (templateState.currentItem?.id === shape.id) return;
    targets.push({
      type: ITEM_TYPES.Shape,
      position: shape.position,
      size: shape.size,
      id: shape.id,
      stroke: shape.stroke,
    });
  });
  if (
    templateState.table &&
    templateState.currentItem?.id !== templateState.table.id
  ) {
    targets.push({
      type: ITEM_TYPES.Table,
      position: templateState.table.position,
      size: templateState.table.size,
      id: templateState.table.id,
      textAlign: templateState.table.textStyle.textAlign,
      verticalAlign: templateState.table.textStyle.verticalAlign,
    });
  }

  // unique items (without id)
  if (
    templateState.presenter.presenter?.face_profile &&
    (chapterType === TemplateType.CHAPTER || templateState.slideLayoutType)
  ) {
    if (templateState.currentItem?.type !== ITEM_TYPES.Presenter) {
      targets.push({
        type: ITEM_TYPES.Presenter,
        position: templateState.presenter.position,
        size: templateState.presenter.size,
        stroke: templateState.presenter.stroke,
      });
    }
  }
  if (
    templateState.textStyle.showSubtitles &&
    chapterType === TemplateType.CHAPTER
  ) {
    if (templateState.currentItem?.type !== ITEM_TYPES.Subtitles) {
      targets.push({
        type: ITEM_TYPES.Subtitles,
        position: templateState.textStyle.position,
        size: templateState.textStyle.size,
      });
    }
  }
  if (
    templateState.titleStyle.showTitle &&
    chapterType === TemplateType.TRANSITION
  ) {
    if (templateState.currentItem?.type !== ITEM_TYPES.Title) {
      targets.push({
        type: ITEM_TYPES.Title,
        position: templateState.titleStyle.position,
        size: templateState.titleStyle.size,
      });
    }
  }
  if (
    templateState.logo.showLogo &&
    templateState.logo.assetId &&
    (chapterType === TemplateType.CHAPTER || templateState.slideLayoutType)
  ) {
    if (templateState.currentItem?.type !== ITEM_TYPES.Logo) {
      targets.push({
        type: ITEM_TYPES.Logo,
        position: templateState.logo.position,
        size: templateState.logo.size,
      });
    }
  }

  return targets;
};
